/* eslint-disable @typescript-eslint/no-use-before-define */
import {
    Action,
    ActionReducerMap,
    createFeatureSelector,
    createSelector,
    StoreModule,
} from '@ngrx/store';
import { Waypoint } from '@polaris-customerweb/sharedcomponents.user-content';
import {
    DeleteWaypoint,
    ForceWaypointRefresh,
    SetActivePublicWaypoint,
    SetActiveWaypoint,
    SetEditableWaypoint,
    SetPublicWaypointsInView,
    SetWaypoints,
    ToggleActiveWaypoint,
    UpdateEditableWaypointLocation,
    UpdatePublicWaypoint,
    UpdateWaypoint,
    WaypointAction,
} from './waypoint.actions';

const featureName = 'waypoint';

export interface WaypointState {
    activePublicWaypoint?: Waypoint;
    activeWaypoint?: Waypoint;
    editableWaypoint?: Waypoint;
    forceRefresh: boolean;
    isWaypointEditing: boolean;
    publicWaypointsInView: Waypoint[];
    waypoints: Waypoint[];
}

const reducerMap: ActionReducerMap<WaypointState> = {
    activePublicWaypoint: activePublicWaypointReducer,
    activeWaypoint: activeWaypointReducer,
    editableWaypoint: editableWaypointReducer,
    forceRefresh: forceRefreshReducer,
    isWaypointEditing: isWaypointEditingReducer,
    publicWaypointsInView: publicWaypointsInViewReducer,
    waypoints: waypointsReducer,
};

export const selectWaypointState =
    createFeatureSelector<WaypointState>(featureName);
export const selectActivePublicWaypoint = createSelector(
    selectWaypointState,
    (state) => state.activePublicWaypoint
);
export const selectActiveWaypoint = createSelector(
    selectWaypointState,
    (state) => state.activeWaypoint
);
export const selectEditableWaypoint = createSelector(
    selectWaypointState,
    (state) => state.editableWaypoint
);
export const selectIsWaypointEditing = createSelector(
    selectWaypointState,
    (state) => state.isWaypointEditing
);
export const selectShouldForceWaypointRefresh = createSelector(
    selectWaypointState,
    (state) => state.forceRefresh
);
export const selectPublicWaypointsInView = createSelector(
    selectWaypointState,
    (state) => state.publicWaypointsInView
);
export const selectWaypoints = createSelector(
    selectWaypointState,
    (state) => state.waypoints
);

export const waypointStoreModule = StoreModule.forFeature(
    featureName,
    reducerMap
);

function activePublicWaypointReducer(state: Waypoint, action: Action) {
    switch (action.type) {
        case WaypointAction.setActivePublicWaypoint:
            return (action as SetActivePublicWaypoint).publicWaypoint;
        default:
            return state;
    }
}

function activeWaypointReducer(state: Waypoint, action: Action) {
    switch (action.type) {
        case WaypointAction.setActiveWaypoint:
            return (action as SetActiveWaypoint).waypoint;
        case WaypointAction.toggleActiveWaypoint:
            if ((action as ToggleActiveWaypoint).waypoint?.id === state.id) {
                return undefined;
            }

            return (action as ToggleActiveWaypoint).waypoint;
        default:
            return state;
    }
}

function editableWaypointReducer(state: Waypoint, action: Action) {
    switch (action.type) {
        case WaypointAction.setEditableWaypoint:
            return (action as SetEditableWaypoint).waypoint?.clone();
        case WaypointAction.updateEditableWaypointLocation:
            if (state.id) {
                const updatedWaypoint = state.clone();
                updatedWaypoint.geoCoordinate = (
                    action as UpdateEditableWaypointLocation
                ).geoCoordinate;
                return updatedWaypoint;
            }

            return state;
        default:
            return state;
    }
}

function forceRefreshReducer(state = true, action: Action) {
    switch (action.type) {
        case WaypointAction.forceWaypointRefresh:
            return (action as ForceWaypointRefresh).forceRefresh;
        default:
            return state;
    }
}

function isWaypointEditingReducer(state = false, action: Action) {
    switch (action.type) {
        case WaypointAction.setEditableWaypoint:
            return Boolean((action as SetEditableWaypoint).waypoint);
        default:
            return state;
    }
}

function publicWaypointsInViewReducer(state: Waypoint[] = [], action: Action) {
    switch (action.type) {
        case WaypointAction.setPublicWaypointsInView:
            return [...(action as SetPublicWaypointsInView).publicWaypoints];
        case WaypointAction.updatePublicWaypoint:
            return [
                ...state.filter(
                    (waypoint) =>
                        waypoint.id !==
                        (action as UpdatePublicWaypoint).publicWaypoint.id
                ),
                (action as UpdatePublicWaypoint).publicWaypoint.clone(),
            ];
        default:
            return state;
    }
}

function waypointsReducer(state: Waypoint[] = [], action: Action) {
    switch (action.type) {
        case WaypointAction.deleteWaypoint:
            return state.filter(
                (waypoint) =>
                    waypoint.id !== (action as DeleteWaypoint).waypointId
            );
        case WaypointAction.setWaypoints:
            return [...(action as SetWaypoints).waypoints];
        case WaypointAction.updateWaypoint:
            return [
                ...state.filter(
                    (waypoint) =>
                        waypoint.id !== (action as UpdateWaypoint).waypoint.id
                ),
                (action as UpdateWaypoint).waypoint.clone(),
            ];
        default:
            return state;
    }
}
